import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    classSchoollApi,
    type ClassSchooll,
    type ClassSchoollFilters,
    type CreateClassSchoollData,
    type UpdateClassSchoollData,
    type ClassSchoollStatistics,
} from "@/api/classSchooll";

export const useClassSchoollStore = defineStore("classSchooll", () => {
    // State
    const classes = ref<ClassSchooll[]>([]);
    const currentClass = ref<ClassSchooll | null>(null);
    const statistics = ref<ClassSchoollStatistics | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const gradeLevel = ref("");
    const major = ref("");
    const majorId = ref("");
    const semesterId = ref("");
    const homeroomTeacherId = ref<string | null>(null);
    const isActive = ref<boolean | null>(null);
    const withAvailableSlots = ref(false);
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const activeClasses = computed(() =>
        classes.value.filter((classItem) => classItem.is_active),
    );

    const classesByGradeLevel = computed(() => {
        const grouped: Record<string, ClassSchooll[]> = {};
        classes.value.forEach((classItem) => {
            const grade = classItem.grade_level;
            if (!grouped[grade]) grouped[grade] = [];
            grouped[grade].push(classItem);
        });
        return grouped;
    });

    const classesByMajor = computed(() => {
        const grouped: Record<string, ClassSchooll[]> = {};
        classes.value.forEach((classItem) => {
            const major = classItem.major?.name || "No Major";
            if (!grouped[major]) grouped[major] = [];
            grouped[major].push(classItem);
        });
        return grouped;
    });

    const classesWithAvailableSlots = computed(() =>
        classes.value.filter((classItem) => !classItem.is_full),
    );

    const fullClasses = computed(() =>
        classes.value.filter((classItem) => classItem.is_full),
    );

    // Actions
    const fetchClasses = async (filters: ClassSchoollFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await classSchoollApi.getAll({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                grade_level: gradeLevel.value || undefined,
                major_id: major.value || majorId.value || undefined,
                semester_id: semesterId.value || undefined,
                homeroom_teacher_id: homeroomTeacherId.value || undefined,
                is_active: isActive.value ?? undefined,
                with_available_slots: withAvailableSlots.value || undefined,
                ...filters,
            });

            classes.value = response.data?.items || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch classes";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClass = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            currentClass.value = (await classSchoollApi.getById(id)).data;
            return currentClass.value;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch class";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createClass = async (data: CreateClassSchoollData) => {
        loading.value = true;
        error.value = null;

        try {
            const newClass = await classSchoollApi.create(data);
            // Refresh list to ensure consistency
            await fetchClasses();
            return newClass;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create class";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateClass = async (id: string, data: UpdateClassSchoollData) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedClass = await classSchoollApi.update(id, data);
            // Refresh list to ensure consistency and all relationships are loaded
            await fetchClasses();
            return updatedClass;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to update class";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteClass = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await classSchoollApi.delete(id);
            classes.value = classes.value.filter((c) => c.id !== id);
            if (currentClass.value?.id === id) {
                currentClass.value = null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete class";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassesByGradeLevel = async (gradeLevel: string) => {
        loading.value = true;
        error.value = null;

        try {
            const classesByGrade =
                await classSchoollApi.getByGradeLevel(gradeLevel);
            return classesByGrade;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch classes by grade level";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassesByMajor = async (major: string) => {
        loading.value = true;
        error.value = null;

        try {
            const classesByMajor = await classSchoollApi.getByMajor(major);
            return classesByMajor;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch classes by major";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassesBySemester = async (semesterId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const classesBySemester =
                await classSchoollApi.getBySemester(semesterId);
            return classesBySemester;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch classes by semester";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassesByHomeroomTeacher = async (teacherId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const classesByTeacher =
                await classSchoollApi.getByHomeroomTeacher(teacherId);
            return classesByTeacher;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch classes by homeroom teacher";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchActiveClasses = async () => {
        loading.value = true;
        error.value = null;

        try {
            const activeClasses = await classSchoollApi.getActive();
            return activeClasses;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch active classes";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchClassesWithAvailableSlots = async () => {
        loading.value = true;
        error.value = null;

        try {
            const availableClasses =
                await classSchoollApi.getWithAvailableSlots();
            return availableClasses;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch classes with available slots";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const searchClasses = async (query: string) => {
        loading.value = true;
        error.value = null;

        try {
            const searchResults = await classSchoollApi.search(query);
            return searchResults;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to search classes";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const assignHomeroomTeacher = async (
        classId: string,
        teacherId: string,
    ) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedClass = await classSchoollApi.assignHomeroomTeacher(
                classId,
                teacherId,
            );
            const index = classes.value.findIndex((c) => c.id === classId);
            if (index !== -1) {
                classes.value[index] = updatedClass.data;
            }
            if (currentClass.value?.id === classId) {
                currentClass.value = updatedClass.data;
            }
            return updatedClass;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to assign homeroom teacher";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const removeHomeroomTeacher = async (classId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedClass =
                await classSchoollApi.removeHomeroomTeacher(classId);
            const index = classes.value.findIndex((c) => c.id === classId);
            if (index !== -1) {
                classes.value[index] = updatedClass.data;
            }
            if (currentClass.value?.id === classId) {
                currentClass.value = updatedClass.data;
            }
            return updatedClass;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to remove homeroom teacher";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const toggleActive = async (classId: string, isActive: boolean) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedClass = await classSchoollApi.toggleActive(
                classId,
                isActive,
            );
            const index = classes.value.findIndex((c) => c.id === classId);
            if (index !== -1) {
                classes.value[index] = updatedClass.data;
            }
            if (currentClass.value?.id === classId) {
                currentClass.value = updatedClass.data;
            }
            return updatedClass;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to toggle class status";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchStatistics = async () => {
        loading.value = true;
        error.value = null;

        try {
            statistics.value = (await classSchoollApi.getStatistics()).data;
            return statistics.value;
        } catch (err: any) {
            error.value =
                err.response?.data?.message ||
                "Failed to fetch class statistics";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
    };

    const resetFilters = async () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        gradeLevel.value = "";
        major.value = "";
        majorId.value = "";
        semesterId.value = "";
        homeroomTeacherId.value = null;
        isActive.value = null;
        withAvailableSlots.value = false;
        await fetchClasses();
    };

    return {
        // State
        classes,
        currentClass,
        statistics,
        loading,
        error,
        page,
        perPage,
        search,
        gradeLevel,
        major,
        majorId,
        semesterId,
        homeroomTeacherId,
        isActive,
        withAvailableSlots,
        meta,

        // Getters
        activeClasses,
        classesByGradeLevel,
        classesByMajor,
        classesWithAvailableSlots,
        fullClasses,

        // Actions
        fetchClasses,
        fetchClass,
        createClass,
        updateClass,
        deleteClass,
        fetchClassesByGradeLevel,
        fetchClassesByMajor,
        fetchClassesBySemester,
        fetchClassesByHomeroomTeacher,
        fetchActiveClasses,
        fetchClassesWithAvailableSlots,
        searchClasses,
        assignHomeroomTeacher,
        removeHomeroomTeacher,
        toggleActive,
        fetchStatistics,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
